/**
 * AnalysisRangeBar.jsx : choix de la plage d'analyse (composant sans état).
 *
 * Raccourcis « Plage commune » (heure réelle) ou « Durée commune » (depuis le
 * début) et « Tout » ; en heure réelle, saisie précise du début et de la fin.
 * Plage commune indisponible (aucun chevauchement) : bouton grisé et
 * explication. Conçu pour resservir sur la page d'une session seule : il ne
 * connaît que la plage résolue (analysisRange.js) et des rappels.
 *
 * Les champs date et heure sont en heure locale du navigateur (D201) : la
 * conversion vers les millisecondes epoch se fait ici, à la frontière de la saisie.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ALIGN_MODES } from '../charts/compareAxis';
import { RANGES } from './analysisRange';

const pad = (n) => String(n).padStart(2, '0');

/** ms epoch → « AAAA-MM-JJTHH:mm » en heure locale (valeur d'un champ datetime-local). */
export function toLocalInput(ms) {
  if (!Number.isFinite(ms)) return '';
  const d = new Date(ms);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Valeur d'un champ datetime-local (heure locale) → ms epoch, ou null. */
export function fromLocalInput(value) {
  if (!value) return null;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
}

export default function AnalysisRangeBar({ resolved, onShortcut, onCustom, buttonStyle, colors }) {
  const { t } = useTranslation();
  const isClock = resolved.align === ALIGN_MODES.CLOCK;
  const commonDisabled = isClock && resolved.commonUnavailable;
  const { start, end } = resolved.clock || {};

  const change = (which) => (e) => {
    const ms = fromLocalInput(e.target.value);
    if (ms == null) return;
    const from = which === 'from' ? ms : start;
    const to = which === 'to' ? ms : end;
    if (from < to) onCustom(from, to);
  };

  const inputStyle = {
    padding: '6px 8px', borderRadius: '8px', fontSize: '13px',
    border: `1px solid ${colors.border}`, background: colors.card, color: colors.text,
  };

  return (
    <div data-testid="analysis-range-bar">
      <div role="group" aria-label={t('sessions.compare.range.label')}
        style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: '13px', fontWeight: '600', color: colors.textSec }}>
          {t('sessions.compare.range.label')}
        </span>
        <button onClick={() => onShortcut(RANGES.COMMON)} disabled={commonDisabled}
          aria-pressed={resolved.range === RANGES.COMMON}
          style={{ ...buttonStyle(resolved.range === RANGES.COMMON), ...(commonDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}) }}>
          {t(isClock ? 'sessions.compare.range.commonClock' : 'sessions.compare.range.commonFromStart')}
        </button>
        <button onClick={() => onShortcut(RANGES.ALL)} aria-pressed={resolved.range === RANGES.ALL}
          style={buttonStyle(resolved.range === RANGES.ALL)}>
          {t('sessions.compare.range.all')}
        </button>
        {isClock && resolved.clock && (
          <>
            <label style={{ fontSize: '13px', color: colors.textSec, display: 'flex', gap: '6px', alignItems: 'center' }}>
              {t('sessions.compare.range.from')}
              <input type="datetime-local" value={toLocalInput(start)} onChange={change('from')} style={inputStyle} />
            </label>
            <label style={{ fontSize: '13px', color: colors.textSec, display: 'flex', gap: '6px', alignItems: 'center' }}>
              {t('sessions.compare.range.to')}
              <input type="datetime-local" value={toLocalInput(end)} onChange={change('to')} style={inputStyle} />
            </label>
          </>
        )}
      </div>
      {commonDisabled && (
        <p data-testid="range-common-unavailable" style={{ fontSize: '12px', color: colors.textSec, margin: '8px 0 0' }}>
          {t('sessions.compare.range.commonUnavailable')}
        </p>
      )}
    </div>
  );
}
